const DEFAULT_LANG_PREFS = 'fr-FR'

export function niceDate(date: number | string | Date) {
  const d = new Date(date)
  const languagePrefs = DEFAULT_LANG_PREFS

  return `${d.toLocaleDateString(languagePrefs, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })} ${'à'} ${d.toLocaleTimeString(languagePrefs, {
    hour: 'numeric',
    minute: '2-digit',
  })}`
}

export function niceDateShort(date: number | string | Date) {
  const d = new Date(date)
  const languagePrefs = DEFAULT_LANG_PREFS
  return `${d.toLocaleDateString(languagePrefs, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })}`
}

export function formatDate(date: Date, locale?: string, utc?: boolean) {
  const appLocale = locale || DEFAULT_LANG_PREFS
  const formatter = new Intl.DateTimeFormat(appLocale, {
    timeZone: utc ? 'UTC' : undefined,
  })

  return formatter.format(date)
}

const NOW = 5
const MINUTE = 60
const HOUR = MINUTE * 60
const DAY = HOUR * 24
const MONTH = DAY * 28
const YEAR = DAY * 365
export function ago(date: number | string | Date): string {
  let ts: number
  if (typeof date === 'string') {
    ts = Number(new Date(date))
  } else if (date instanceof Date) {
    ts = Number(date)
  } else {
    ts = date
  }
  const diffSeconds = Math.floor((Date.now() - ts) / 1e3)
  if (diffSeconds < NOW) {
    return "à l'instant"
  } else if (diffSeconds < MINUTE) {
    return `Il y a ${diffSeconds}s`
  } else if (diffSeconds < HOUR) {
    const minutes = Math.floor(diffSeconds / MINUTE)
    return `Il y a ${minutes}m`
  } else if (diffSeconds < DAY) {
    const hours = Math.floor(diffSeconds / HOUR)
    return `Il y a ${hours}h`
  } else if (diffSeconds < MONTH) {
    const days = Math.round(diffSeconds / DAY)
    return `Il y a ${days}d`
  } else if (diffSeconds < YEAR) {
    const months = Math.floor(diffSeconds / MONTH)
    return `Il y a ${months}mo`
  } else {
    return new Date(ts).toLocaleDateString()
  }
}

export function age(date: number | string | Date) {
  const d = new Date(date)
  const now = new Date()
  const diff = now.getTime() - d.getTime()
  const timeDifference = new Date(diff)
  return Math.abs(timeDifference.getUTCFullYear() - 1970)
}

export async function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export function getCurrentLocaleYear() {
  return new Date().getFullYear().toLocaleString()
}

type RewindOptions = {
  days?: number
  weeks?: number
  months?: number
  years?: number
}

export function rewindDate(date: Date, opt: RewindOptions = {}) {
  const { days = 0, weeks = 0, months = 0, years = 0 } = opt
  const d = new Date(date)
  d.setDate(d.getDate() - days)
  d.setDate(d.getDate() - weeks * 7)
  d.setMonth(d.getMonth() - months)
  d.setFullYear(d.getFullYear() - years)
  return d
}

export function calcApproxDueDate(date: Date | string | number, eta: number, etaUnit: 'day' | 'month' | 'week') {
  const d = new Date(date)
  let duration = 0

  switch (etaUnit) {
    case 'day':
      duration = eta * DAY
      break
    case 'month':
      duration = eta * MONTH
      break
    case 'week':
      duration = eta * DAY * 7
      break
    default:
      break
  }

  return new Date(d.getTime() + duration * 1000)
}

export function msToTime(ms: number | string) {
  if (typeof ms === 'string') {
    ms = parseInt(ms, 10)
  }
  return new Date(ms * 1000).toISOString().substring(11, 16)
}

export function formatSecondsToTime(seconds: number) {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60

  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

export function sqlTimeToMs(time: string) {
  const parts = time.split(':')
  if (parts.length !== 3) {
    throw new Error('Invalid time format. Expected HH:MM:SS')
  }
  const hours = parseInt(parts[0], 10)
  const minutes = parseInt(parts[1], 10)
  const seconds = parseInt(parts[2], 10)

  return (hours * 3600 + minutes * 60 + seconds) * 1000
}

export function daysUntil(date: Date | string | number) {
  const now = new Date()
  const targetDate = new Date(date)
  const diffTime = targetDate.getTime() - now.getTime()
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
}
